import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import {
  deleteLeagueUseCase,
  getLeagueParticipantsUseCase,
  getUserLeaguesUseCase,
} from '@/src/core/dependencies';
import { League } from '@/src/domain/models';
import { useTranslation } from '@/src/i18n';
import { LeaguesScreen } from '@/src/presentation/screens/LeaguesScreen';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';
import { useFocusEffect } from '@react-navigation/native';
import { useRouter } from 'expo-router';
import { useCallback, useState } from 'react';

export default function LeaguesContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [leagueStats, setLeagueStats] = useState<Map<string, { tournamentCount: number; participantCount: number }>>(new Map());
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState<League | null>(null);

  useFocusEffect(
    useCallback(() => {
      loadLeagues();
    }, [user?.id])
  );

  const loadLeagues = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const data = await getUserLeaguesUseCase.execute(user.id);
      setLeagues(data);

      // Load stats for each league
      const statsMap = new Map<string, { tournamentCount: number; participantCount: number }>();
      for (const league of data) {
        const participants = await getLeagueParticipantsUseCase.execute(league.id);
        statsMap.set(league.id, {
          tournamentCount: 0, // Will be computed from tournaments with league_id
          participantCount: participants.length,
        });
      }
      setLeagueStats(statsMap);
    } catch (error) {
      logger.error('Leagues', 'Failed to load leagues', { error });
      platformAlert(t('common.error'), t('leagues.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handleLeaguePress = (leagueId: string) => {
    router.push(`/league/${leagueId}`);
  };

  const handleCreateLeague = () => {
    router.push('/create-league');
  };

  const handleMenuPress = (league: League) => {
    setSelectedLeague(league);
    setMenuVisible(true);
  };

  const handleEdit = () => {
    if (selectedLeague) {
      router.push(`/league/${selectedLeague.id}`);
    }
  };

  const handleDelete = () => {
    if (!selectedLeague) return;

    platformAlert(
      t('leagues.deleteLeague'),
      t('leagues.confirmDeleteLeague'),
      [
        { text: t('common.cancel'), style: 'cancel' },
        {
          text: t('common.delete'),
          style: 'destructive',
          onPress: async () => {
            try {
              await deleteLeagueUseCase.execute(selectedLeague.id);
              setLeagues((prev) => prev.filter((l) => l.id !== selectedLeague.id));
              platformAlert(t('common.success'), t('leagues.leagueDeleted'));
            } catch (error) {
              logger.error('Leagues', 'Failed to delete league', { error });
              platformAlert(t('common.error'), t('leagues.failedToDelete'));
            }
          },
        },
      ]
    );
  };

  return (
    <LeaguesScreen
      leagues={leagues}
      leagueStats={leagueStats}
      loading={loading}
      searchQuery={searchQuery}
      setSearchQuery={setSearchQuery}
      menuVisible={menuVisible}
      setMenuVisible={setMenuVisible}
      handleLeaguePress={handleLeaguePress}
      handleCreateLeague={handleCreateLeague}
      handleMenuPress={handleMenuPress}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      onRefresh={loadLeagues}
      colors={colors}
    />
  );
}
